/**
 * The Reports page's colours — FIVE FAMILIES, one per programme area (2026-10-15 redesign):
 *
 *   risk       coral   clicking, entering data, repeat clickers — what puts the organisation at risk
 *   protect    teal    reporting, reports that held up — the behaviour the programme builds
 *   knowledge  violet  assessments and exams
 *   training   blue    programmes, learners, bulletins
 *   policy     amber   policy acknowledgement
 *
 * Every indicator wears its area's colour (a second indicator of the same area takes the family's
 * deep tone), so a reader learns five colours, not fourteen, and a chart is read by its colour
 * before its legend. The tones share one lightness and chroma, so no family shouts over another.
 * Colour is for MARKS only — lines, bars, dots, an icon's chip — never a card's surface.
 *
 * A canvas cannot read a custom property and these hues are the Reports page's own, so they live
 * here as light and dark pairs; `hue()` returns the pair for the theme on screen. The pages call
 * `useTheme()`, so a theme switch re-renders them and every hue is read again.
 *
 * Better / worse (a change, a department behind) use teal and coral — the protect and risk families.
 */

const HUES = {
  risk: ['#e5645c', '#ff8b82'],
  riskDeep: ['#c2453f', '#ff6f66'],
  protect: ['#0f9f8a', '#34c9b0'],
  protectDeep: ['#0b7d6d', '#5bdcc4'],
  knowledge: ['#7768ea', '#a397ff'],
  knowledgeDeep: ['#5747c9', '#bdb3ff'],
  training: ['#3a8bdc', '#6aaef5'],
  trainingDeep: ['#25679f', '#93c3f7'],
  policy: ['#e39b32', '#f5bc5e'],
  /* Older names, kept for the charts that ask for them, mapped onto the families. */
  coral: ['#e5645c', '#ff8b82'],
  rose: ['#c2453f', '#ff6f66'],
  teal: ['#0f9f8a', '#34c9b0'],
  deepTeal: ['#0b7d6d', '#2fb8a1'],
  green: ['#0f9f8a', '#34c9b0'],
  orange: ['#e5645c', '#ff8b82'],
  indigo: ['#7768ea', '#a397ff'],
  lavender: ['#7768ea', '#a397ff'],
  lavenderSoft: ['#cbc4f8', '#4f4890'],
  pink: ['#5747c9', '#bdb3ff'],
  blue: ['#3a8bdc', '#6aaef5'],
  blueSoft: ['#a8cbf1', '#3d6690'],
  cyan: ['#3a8bdc', '#6aaef5'],
  lime: ['#25679f', '#93c3f7'],
  amber: ['#e39b32', '#f5bc5e'],
  slate: ['#a3aab6', '#6b7280'],
  slateSoft: ['#dde1e8', '#363d4b'],
}

/** Which family each indicator wears. A key with no hue of its own falls back to training blue. */
const BY_METRIC = {
  clickRate: 'risk',
  submitRate: 'riskDeep',
  employeesClicked: 'risk',
  reportRate: 'protect',
  reportedWithoutClick: 'protectDeep',
  reporterParticipation: 'protect',
  validShare: 'protectDeep',
  assessmentParticipation: 'knowledge',
  assessmentPass: 'knowledgeDeep',
  examFirstAttemptPass: 'knowledge',
  trainingCompletion: 'training',
  trainingStarted: 'training',
  activeLearners: 'trainingDeep',
  policyAcknowledgement: 'policy',
}

/** The follow-up lists, each in the family of the indicator it follows up. */
const BY_LIST = {
  clicked: 'risk',
  repeatClickers: 'riskDeep',
  overdueTraining: 'training',
  pendingPolicies: 'policy',
  assessmentNotTaken: 'knowledge',
  failedAssessment: 'knowledgeDeep',
}

const dark = () => typeof document !== 'undefined' && document.documentElement.dataset.theme === 'dark'

/** A named hue for the theme on screen. */
export const hue = (name) => (HUES[name] ?? HUES.training)[dark() ? 1 : 0]

/** A named hue as it prints on white paper, whatever theme is on screen — for the exported documents. */
export const paperHue = (name) => (HUES[name] ?? HUES.training)[0]

/** A follow-up list's hue on paper. */
export const paperListHue = (list) => paperHue(BY_LIST[list] ?? 'slate')

/** An indicator's hue. */
export const metricHue = (key) => hue(BY_METRIC[key] ?? 'training')

/** A follow-up list's hue. */
export const listHue = (list) => hue(BY_LIST[list] ?? 'slate')

/** Better and worse. */
export const goodHue = () => hue('protect')
export const badHue = () => hue('risk')

/**
 * The outcome states as the reports draw them, after the reference boards: done in a deep teal,
 * under way in a soft blue, late or partial in amber, failed in coral, nothing yet in a pale grey.
 */
export const statusHues = () => ({
  success: hue('deepTeal'),
  failure: hue('coral'),
  incomplete: hue('amber'),
  absent: hue('slateSoft'),
  info: hue('blueSoft'),
  infoSoft: hue('cyan'),
  /* The CSS-class spelling the incident outcomes use. */
  'info-soft': hue('cyan'),
})

/** `#rrggbb` at an alpha. */
export const tint = (hex, alpha) => {
  const n = parseInt(hex.slice(1), 16)
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`
}

/* Relative luminance of `#rrggbb`, for choosing the ink of a figure printed inside a fill. */
const luminance = (hex) => {
  const n = parseInt(hex.slice(1), 16)
  const [r, g, b] = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((c) => {
    const v = c / 255
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4
  })
  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

/** Ink for a figure printed INSIDE `fill`: white on a deep fill, near-black on a pale one. */
export const inkOn = (fill) => (luminance(fill) > 0.32 ? '#1b2333' : '#ffffff')
